/* eslint-disable no-restricted-syntax */

/**
 * Restores external links when the page is served through Google Translate's
 * `.translate.goog` proxy. The proxy rewrites all hrefs (including external
 * ones) to route through `translate.google.com/website?…&u=ORIGINAL_URL`.
 * This utility detects those rewritten links and sets the href back to the
 * original URL so users navigate directly to the external site.
 */

const TRANSLATE_GOOG_SUFFIX = '.translate.goog';

function isTranslatedPage() {
  return window.location.hostname.endsWith(TRANSLATE_GOOG_SUFFIX);
}

function getSitePrefix() {
  return window.location.hostname.replace(TRANSLATE_GOOG_SUFFIX, '');
}

function fixLink(link) {
  const href = link.getAttribute('href');
  if (!href) return;

  let url;
  try {
    url = new URL(href, window.location.origin);
  } catch {
    return;
  }

  // Pattern: translate.google.com/website?sl=…&tl=…&u=ORIGINAL_URL
  if (url.hostname === 'translate.google.com' && url.pathname === '/website') {
    const originalUrl = url.searchParams.get('u');
    if (originalUrl) {
      link.setAttribute('href', originalUrl);
    }
    return;
  }

  // Pattern: external-site.translate.goog (not our own site)
  if (url.hostname.endsWith(TRANSLATE_GOOG_SUFFIX)) {
    const prefix = url.hostname.replace(TRANSLATE_GOOG_SUFFIX, '');
    if (prefix === getSitePrefix()) return;

    const params = url.searchParams;
    params.delete('_x_tr_sl');
    params.delete('_x_tr_tl');
    params.delete('_x_tr_hl');
    params.delete('_x_tr_pto');
    params.delete('_x_tr_hist');
    params.delete('_x_tr_enc');

    const originalHost = prefix.replace(/-/g, '.');
    const query = params.toString();
    const path = url.pathname + (query ? `?${query}` : '') + url.hash;
    link.setAttribute('href', `https://${originalHost}${path}`);
  }
}

function fixAllExternalLinks(root = document) {
  if (!isTranslatedPage()) return;

  const links = root.querySelectorAll('a[href]');
  links.forEach(fixLink);
}

fixAllExternalLinks();

// Also observe dynamically added links (e.g. from lazy-loaded content)
const observer = new MutationObserver(mutations => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      // eslint-disable-next-line no-continue
      if (node.nodeType !== Node.ELEMENT_NODE) continue;
      if (node.tagName === 'A' && node.hasAttribute('href')) {
        fixLink(node);
      }
      const nested = node.querySelectorAll?.('a[href]');
      if (nested) nested.forEach(fixLink);
    }
  }
});

if (isTranslatedPage()) {
  observer.observe(document.body, { childList: true, subtree: true });
}
